/**
 * HC Cart Drawer - Uses shared HC Cart Utils
 * Includes visual loading cues and proper error handling
 */

class HcCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  connectedCallback() {
    // Initialize cart state when element is connected to DOM
    this.initializeCartState();
    
    // Add event listeners for cart item interactions
    this.addEventListener('click', this.handleCartItemClick.bind(this));
    
    // Initialize quantity inputs and remove buttons
    this.initializeCartComponents();
  }

  async initializeCartState() {
    // Check if cart has items and update the display accordingly
    const cartData = await this.fetchCart();
    if (cartData) {
      if (cartData.items && cartData.items.length > 0) {
        this.classList.remove('is-empty');
        // Populate cart items without opening the drawer
        await this.renderContents(cartData, false);
      } else {
        this.classList.add('is-empty');
      }
    }
  }

  initializeCartComponents() {
    // Setup quantity inputs
    const quantityInputs = this.querySelectorAll('quantity-input');
    quantityInputs.forEach(input => {
      if (!input.hcInitialized) {
        this.setupQuantityInput(input);
      }
    });

    // Setup remove buttons
    const removeButtons = this.querySelectorAll('hc-cart-remove-button');
    removeButtons.forEach(button => {
      if (!button.hcInitialized) {
        this.setupRemoveButton(button);
      }
    });
  }

  setupQuantityInput(element) {
    // Use shared utility with drawer-specific options
    window.HCCartUtils.setupQuantityInput(element, {
      onUpdate: (line, quantity, name, variantId) => {
        this.updateQuantity(line, quantity, name, variantId);
      }
    });
  }

  setupRemoveButton(element) {
    // Use shared utility with drawer-specific options
    window.HCCartUtils.setupRemoveButton(element, {
      onRemove: (itemIndex) => {
        this.removeCartItem(itemIndex);
      }
    });
  }

  async updateQuantity(line, quantity, name, variantId) {
    console.log('Cart drawer updating quantity:', { line, quantity, name, variantId });
    
    // Show loading state
    this.showLoadingState(line);
    
    // Use shared utility for the actual update
    await window.HCCartUtils.updateQuantity(line, quantity, name, variantId, {
      onSuccess: async (parsedState) => {
        console.log('Cart drawer update successful, refreshing contents');
        await this.renderContents(parsedState, false);
        this.hideLoadingState(line);
      },
      onEmpty: async () => {
        console.log('Cart drawer is empty, updating display');
        this.classList.add('is-empty');
        await this.renderContents({ items: [] }, false);
      }
    });
  }

  async removeCartItem(itemIndex) {
    console.log('Cart drawer removing item:', itemIndex);
    
    // Show loading state for remove button
    this.showRemoveButtonLoading(itemIndex);
    
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: itemIndex,
          quantity: 0
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        
        if (cartData.items && cartData.items.length > 0) {
          await this.renderContents(cartData, false);
        } else {
          this.classList.add('is-empty');
          await this.renderContents({ items: [] }, false);
        }
      } else {
        console.error('Failed to remove item from cart');
        this.showError('Failed to remove item. Please try again.');
      }
    } catch (error) {
      console.error('Error removing cart item:', error);
      this.showError('Network error. Please try again.');
    } finally {
      this.hideRemoveButtonLoading(itemIndex);
    }
  }

  showLoadingState(itemIndex) {
    // Show loading overlay for specific item
    const cartItem = this.querySelector(`#CartItem-${itemIndex}`);
    if (cartItem) {
      const loadingOverlay = cartItem.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('hidden');
      }
    }

    // Disable quantity buttons for this item
    const quantityButtons = this.querySelectorAll(`#CartItem-${itemIndex} button[name="plus"], #CartItem-${itemIndex} button[name="minus"]`);
    quantityButtons.forEach(button => {
      button.disabled = true;
      button.classList.add('disabled');
    });
  }

  hideLoadingState(itemIndex) {
    // Hide loading overlay for specific item
    const cartItem = this.querySelector(`#CartItem-${itemIndex}`);
    if (cartItem) {
      const loadingOverlay = cartItem.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
    }

    // Re-enable quantity buttons for this item
    const quantityButtons = this.querySelectorAll(`#CartItem-${itemIndex} button[name="plus"], #CartItem-${itemIndex} button[name="minus"]`);
    quantityButtons.forEach(button => {
      button.disabled = false;
      button.classList.remove('disabled');
    });
  }

  showRemoveButtonLoading(itemIndex) {
    const removeButton = this.querySelector(`#CartItem-${itemIndex} hc-cart-remove-button button`);
    if (removeButton) {
      removeButton.disabled = true;
      removeButton.classList.add('loading');
      removeButton.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="loading-spinner">
          <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
          <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" values="0 8 8;360 8 8"/>
          </path>
        </svg>
      `;
    }
  }

  hideRemoveButtonLoading(itemIndex) {
    const removeButton = this.querySelector(`#CartItem-${itemIndex} hc-cart-remove-button button`);
    if (removeButton) {
      removeButton.disabled = false;
      removeButton.classList.remove('loading');
      removeButton.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  }

  showError(message) {
    // Create or update error display
    let errorElement = this.querySelector('.hc-cart-drawer__error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'hc-cart-drawer__error';
      errorElement.style.cssText = `
        background: #fee;
        color: #c33;
        padding: 1rem;
        margin: 1rem;
        border-radius: 0.5rem;
        border: 1px solid #fcc;
        text-align: center;
      `;
      this.querySelector('.hc-cart-drawer__content').prepend(errorElement);
    }
    
    errorElement.textContent = message;
    errorElement.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }, 5000);
  }

  open() {
    // Remove conflicting classes and add our class
    this.classList.remove('flex', 'drawer', 'drawer--cart', 'h100');
    this.classList.add('hc-cart-drawer', 'active');
    document.body.classList.add('hc-cart-drawer-open');
    
    // Re-initialize components when opening
    this.initializeCartComponents();
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('hc-cart-drawer-open');
  }

  async fetchCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      return null;
    }
  }

  async renderContents(cartData = null, shouldOpen = true) {
    console.log('HC Cart Drawer renderContents called with:', cartData);
    // If no cart data provided, fetch it
    if (!cartData) {
      cartData = await this.fetchCart();
    }
    
    if (cartData) {
      // Update empty state based on cart data
      if (cartData.items && cartData.items.length > 0) {
        this.classList.remove('is-empty');
        
        // Update cart items
        const cartItemsContainer = this.querySelector('.hc-cart-drawer__items');
        if (cartItemsContainer) {
          // Build cart items HTML from the cart data
          let cartItemsHTML = '';
          cartData.items.forEach((item, index) => {
            const itemIndex = index + 1;
            cartItemsHTML += `
              <div class="hc-cart__item" id="CartItem-${itemIndex}">
                <div class="loading-overlay hidden">
                  <div class="loading-spinner"></div>
                </div>
                
                <div class="hc-cart__item-image">
                  ${item.image ? `
                    <a href="${item.url}" aria-hidden="true" tabindex="-1">
                      <img src="${item.image}" alt="${item.title}" loading="lazy" width="120" height="120">
                    </a>
                  ` : ''}
                </div>

                <div class="hc-cart__item-details">
                  <!-- Top Row: Title (center) and Quantity (right) -->
                  <div class="hc-cart__top-row">
                    <a href="${item.url}" class="hc-cart__item-name">${item.product_title}</a>
                    <div class="hc-cart__quantity-wrapper">
                      <label class="visually-hidden" for="Quantity-${itemIndex}">
                        Quantity
                      </label>

                      <quantity-input class="quantity hc-cart__quantity-input-wrapper">
                        <input class="hc-cart__quantity-input"
                          type="number"
                          name="updates[]"
                          value="${item.quantity}"
                          id="Quantity-${itemIndex}"
                          min="0"
                          step="1"
                          data-index="${itemIndex}"
                          data-quantity-variant-id="${item.variant_id}"
                          aria-label="Quantity for ${item.product_title}"
                        >
                        <div class="hc-cart__quantity-buttons">    
                          <button class="hc-cart__quantity-btn" name="plus" type="button">
                            <span class="visually-hidden">Increase quantity</span>
                            <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.6666 5.33331L5.99992 0.666646L1.33325 5.33331" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </button>
                          <button class="hc-cart__quantity-btn" name="minus" type="button">
                            <span class="visually-hidden">Decrease quantity</span>
                            <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.33325 0.666646L5.99992 5.33331L10.6666 0.666646" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </quantity-input>
                    </div>
                  </div>

                  <!-- Bottom Row: Price (center) and Remove (right) -->
                  <div class="hc-cart__bottom-row">
                    <div class="hc-cart__item-price">
                      ${item.original_price !== item.final_price ? `
                        <s>${this.formatMoney(item.original_price)}</s>
                        <strong>${this.formatMoney(item.final_price)}</strong>
                      ` : this.formatMoney(item.original_price)}
                    </div>
                    
                    <hc-cart-remove-button data-index="${itemIndex}">
                      <button class="hc-cart__remove-btn" aria-label="Remove ${item.product_title}">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                    </hc-cart-remove-button>
                  </div>

                  ${item.variant_title && item.variant_title !== 'Default Title' ? `
                    <div class="product-options">
                      <div class="product-option">
                        <span class="option-name">Variant:</span>
                        <span class="option-value">${item.variant_title}</span>
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
          });
          
          cartItemsContainer.innerHTML = cartItemsHTML;
          
          // Update subtotal
          this.updateSubtotal(cartData);
          
          // Re-initialize components after rendering
          this.initializeCartComponents();
          
          // Hide all loading overlays after rendering
          this.hideAllLoadingOverlays();
        }
      } else {
        this.classList.add('is-empty');
        const cartItemsContainer = this.querySelector('.hc-cart-drawer__items');
        if (cartItemsContainer) {
          cartItemsContainer.innerHTML = '';
        }
      }
    }
    
    // Open the drawer if requested
    if (shouldOpen) {
      this.open();
    }
  }

  handleCartItemClick(event) {
    // This method is kept for backward compatibility but the actual handling
    // is now done by the shared utilities through the component setup
    console.log('Cart drawer item clicked:', event.target);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateSubtotal(cartData) {
    const subtotalElement = this.querySelector('.cart-subtotal');
    if (subtotalElement && cartData.total_price) {
      subtotalElement.textContent = this.formatMoney(cartData.total_price);
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  hideAllLoadingOverlays() {
    const loadingOverlays = this.querySelectorAll('.loading-overlay');
    loadingOverlays.forEach(overlay => {
      overlay.classList.add('hidden');
    });
  }
}

customElements.define('hc-cart-drawer', HcCartDrawer);